import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@bruno/ui';
import type {
  AgentServerMsg,
  ConversationDetail,
  ConversationListItem,
  InboxMessage,
} from '@bruno/shared';
import {
  claimConversation,
  claimPendingConversation,
  getInboxConversation,
  handbackConversation,
  listInboxConversations,
  postAgentMessage,
  reopenConversation,
  resolveConversation,
  type ListInboxParams,
} from '../api/inbox';
import { AgentSocket, type AgentSocketState } from '../lib/agentSocket';
import { agentNotifier } from '../lib/notify';
import { useAuth } from '../auth/AuthContext';

type AssigneeFilter = 'any' | 'me' | 'unassigned';
type StatusFilter = 'open' | 'resolved' | 'all';

export function InboxPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [assignee, setAssignee] = useState<AssigneeFilter>('any');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [socketState, setSocketState] = useState<AgentSocketState>('connecting');
  const socketRef = useRef<AgentSocket | null>(null);

  const params: ListInboxParams = useMemo(
    () => ({
      assignee,
      status: statusFilter,
      q: search.trim() ? search.trim() : undefined,
    }),
    [assignee, statusFilter, search],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['inbox', 'list', params],
    queryFn: () => listInboxConversations(params),
    refetchOnWindowFocus: false,
  });

  // Track which conversations are already known so we can tell a brand-
  // new agent-assignment apart from a routine row update. We use refs
  // (not state) because nothing in the JSX depends on this; we just
  // need it inside the WS handler.
  const knownConversationsRef = useRef<Map<string, KnownConvSnapshot>>(new Map());
  const selectedRef = useRef<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedRef.current = selected;
    if (selected) agentNotifier.acknowledge();
  }, [selected]);
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  // Reset unread / title when the user comes back to the tab while a
  // specific conversation is selected — they're presumed to be reading.
  useEffect(() => {
    function onVisible(): void {
      if (typeof document === 'undefined') return;
      if (!document.hidden && selectedRef.current) agentNotifier.clear();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, []);

  // Wire up the agent WebSocket once.
  useEffect(() => {
    // Best-effort permission grab: on the first inbox mount we ask the
    // browser for OS-level notification rights. The Notification API
    // itself caches the user's choice across sessions; we never re-ask
    // if they've already declined.
    agentNotifier.requestPermission();

    const socket = new AgentSocket({
      onState: (s) => setSocketState(s),
      onMessage: (msg) =>
        handleSocketMessage(msg, qc, {
          known: knownConversationsRef.current,
          selectedId: selectedRef.current,
          currentUserId: currentUserIdRef.current,
          onSelect: (id) => setSelected(id),
        }),
    });
    socketRef.current = socket;
    void socket.connect();
    return () => socket.close();
  }, [qc]);

  const conversations = data?.conversations ?? [];
  // Split out pending handoffs so they pin to the top of the list with
  // a per-row Take button. Server already orders by recency; we just
  // partition.
  const pendingConvs = conversations.filter((c) => c.handler === 'queued' && !c.endedAt);
  const otherConvs = conversations.filter((c) => !pendingConvs.includes(c));

  const claimPending = useMutation({
    mutationFn: (id: string) => claimPendingConversation(id),
    onSuccess: (_resp, id) => {
      setSelected(id);
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', id] });
    },
    onError: () => {
      // 409 race-lost is expected; just refresh so the row disappears
      // when the winner's broadcast reaches us.
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
    },
  });

  // Snapshot the current list into the "known" map every time the
  // server data changes. The WS handler reads from this map to decide
  // whether an `inbox.update` represents a brand-new conversation
  // (notify) or just a status update on a chat we already had on
  // screen (don't re-notify).
  useEffect(() => {
    if (!conversations.length) return;
    const next = knownConversationsRef.current;
    for (const c of conversations) {
      next.set(c.id, {
        handler: c.handler,
        assignedAgentId: c.assignedAgentId,
        agentUnread: c.agentUnread,
        endedAt: c.endedAt,
      });
    }
  }, [conversations]);

  const detailQuery = useQuery({
    queryKey: ['inbox', 'conv', selected],
    queryFn: () => (selected ? getInboxConversation(selected) : null),
    enabled: !!selected,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Inbox</h1>
          <p className="text-sm text-slate-500">All conversations across your websites.</p>
        </div>
        <SocketStatePill state={socketState} />
      </div>
      <div className="flex flex-1 gap-4 min-h-0">
        <Card className="w-[360px] flex flex-col p-0 overflow-hidden">
          <div className="border-b border-slate-200 p-3 flex flex-col gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transcripts"
              className="h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            <div className="flex items-center justify-between gap-2">
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value as AssigneeFilter)}
                className="h-8 flex-1 rounded-md border border-slate-300 px-2 text-xs"
              >
                <option value="any">Anyone</option>
                <option value="me">Mine</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="h-8 flex-1 rounded-md border border-slate-300 px-2 text-xs"
              >
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-sm text-slate-500">Loading…</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No conversations match.</div>
            ) : (
              <>
                {pendingConvs.length > 0 ? (
                  <div className="border-b border-amber-200 bg-amber-50/60">
                    <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                      Pending handoffs ({pendingConvs.length})
                    </div>
                    <ul className="divide-y divide-amber-100">
                      {pendingConvs.map((c) => (
                        <li key={c.id}>
                          <PendingHandoffRow
                            item={c}
                            selected={selected === c.id}
                            takePending={claimPending.isPending && claimPending.variables === c.id}
                            onTake={() => claimPending.mutate(c.id)}
                            onSelect={() => setSelected(c.id)}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <ul className="divide-y divide-slate-100">
                  {otherConvs.map((c) => (
                    <li key={c.id}>
                      <ConversationRow
                        item={c}
                        selected={selected === c.id}
                        currentUserId={user?.id}
                        onSelect={() => setSelected(c.id)}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>

        <Card className="flex-1 flex flex-col p-0 overflow-hidden">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-slate-500 text-sm">
              Select a conversation to read it.
            </div>
          ) : detailQuery.isLoading ? (
            <div className="flex-1 grid place-items-center text-slate-500 text-sm">Loading…</div>
          ) : !detailQuery.data ? (
            <div className="flex-1 grid place-items-center text-slate-500 text-sm">
              Conversation not found.
            </div>
          ) : (
            <ConversationView
              detail={detailQuery.data}
              currentUserId={user?.id ?? ''}
              currentUserName={user?.name ?? user?.email ?? 'You'}
              socket={socketRef.current}
              onAfterAction={() => {
                void detailQuery.refetch();
                void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
              }}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function PendingHandoffRow({
  item,
  selected,
  takePending,
  onTake,
  onSelect,
}: {
  item: ConversationListItem;
  selected: boolean;
  takePending: boolean;
  onTake: () => void;
  onSelect: () => void;
}) {
  return (
    <div className={`px-3 py-2.5 ${selected ? 'bg-amber-100/70' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <button
          onClick={onSelect}
          className="text-xs font-medium text-amber-900 hover:underline truncate flex-1 text-left"
        >
          {item.websiteName}
        </button>
        <Badge tone="amber" className="text-[10px]">
          Pending
        </Badge>
      </div>
      <div className="text-sm text-slate-900 truncate">
        {item.lastMessagePreview ?? '(visitor requested a human)'}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" onClick={onTake} loading={takePending}>
          Take
        </Button>
        <button
          onClick={onSelect}
          className="text-xs text-amber-800 hover:underline"
        >
          Open transcript
        </button>
        <span className="ml-auto text-xs text-slate-500">
          {formatTime(item.lastMessageAt ?? item.startedAt)}
        </span>
      </div>
    </div>
  );
}

function ConversationRow({
  item,
  selected,
  currentUserId,
  onSelect,
}: {
  item: ConversationListItem;
  selected: boolean;
  currentUserId: string | undefined;
  onSelect: () => void;
}) {
  const handlerLabel: Record<typeof item.handler, string> = {
    bruno: 'Bruno',
    queued: 'Queued',
    agent: 'Agent',
    offline_message: 'Offline',
  } as const;
  const handlerTone: Record<typeof item.handler, 'gray' | 'amber' | 'green' | 'indigo'> = {
    bruno: 'gray',
    queued: 'amber',
    agent: 'green',
    offline_message: 'indigo',
  } as const;
  const mine = currentUserId && item.assignedAgentId === currentUserId;
  const isResolved = !!item.endedAt;
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${selected ? 'bg-indigo-50' : ''} ${
        isResolved ? 'opacity-75' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-700 truncate">{item.websiteName}</span>
        <div className="flex items-center gap-1 shrink-0">
          {isResolved ? (
            <Badge tone="gray" className="text-[10px]">
              Resolved
            </Badge>
          ) : (
            <Badge tone={handlerTone[item.handler]} className="text-[10px]">
              {handlerLabel[item.handler]}
            </Badge>
          )}
        </div>
      </div>
      {/* NOTE: Always render server-provided strings as text nodes (not innerHTML)
          to keep visitor-controlled content from executing as markup. */}
      <div className="text-sm text-slate-900 truncate">
        {item.lastMessagePreview || item.offlineContact?.message || '(no messages yet)'}
      </div>
      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
        <span>{item.assignedAgentName ?? 'Unassigned'}</span>
        {mine ? <Badge tone="indigo" className="text-[10px]">Mine</Badge> : null}
        {item.agentUnread > 0 ? <Badge tone="red" className="text-[10px]">{item.agentUnread}</Badge> : null}
        <span className="ml-auto">{formatTime(item.lastMessageAt ?? item.startedAt)}</span>
      </div>
    </button>
  );
}

function ConversationView({
  detail,
  currentUserId,
  currentUserName,
  socket,
  onAfterAction,
}: {
  detail: ConversationDetail;
  currentUserId: string;
  currentUserName: string;
  socket: AgentSocket | null;
  onAfterAction: () => void;
}) {
  const conv = detail.conversation;
  const [composer, setComposer] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveReason, setResolveReason] = useState('');
  const send = useMutation({
    mutationFn: (content: string) => postAgentMessage(conv.id, { content }),
    onSuccess: () => {
      setComposer('');
      onAfterAction();
    },
  });
  const claim = useMutation({
    mutationFn: () => claimConversation(conv.id),
    onSuccess: onAfterAction,
  });
  const handback = useMutation({
    mutationFn: () => handbackConversation(conv.id),
    onSuccess: onAfterAction,
  });
  const resolve = useMutation({
    mutationFn: (reason: string) => resolveConversation(conv.id, reason ? { reason } : {}),
    onSuccess: () => {
      setShowResolveForm(false);
      setResolveReason('');
      onAfterAction();
    },
  });
  const reopen = useMutation({
    mutationFn: () => reopenConversation(conv.id),
    onSuccess: onAfterAction,
  });

  const isResolved = !!conv.endedAt;
  const isMine = conv.assignedAgentId === currentUserId;
  const lockOpen = conv.assignmentLockAt ? new Date(conv.assignmentLockAt).getTime() > Date.now() : false;
  const canSend = !isResolved && conv.handler === 'agent' && (isMine || lockOpen);
  // Resolution authorization mirrors the server: any team member can
  // resolve when handler != 'agent'; otherwise it's the assignee, the
  // owner, or anyone within the steal window.
  const canResolve =
    !isResolved &&
    (conv.handler !== 'agent' || isMine || lockOpen);

  // Throttle typing notifications to ≤1/sec; emit `typing off` after a short idle.
  const lastTypingSentRef = useRef(0);
  const typingOffTimerRef = useRef<number | null>(null);
  function notifyTyping(on: boolean) {
    if (!socket || !canSend) return;
    const now = Date.now();
    if (on && now - lastTypingSentRef.current < 1000) return;
    lastTypingSentRef.current = now;
    socket.send({ type: 'agent.typing', conversationId: conv.id, on });
  }
  function handleComposerChange(value: string) {
    setComposer(value);
    if (!canSend) return;
    if (value.trim().length > 0) {
      notifyTyping(true);
      if (typingOffTimerRef.current !== null) window.clearTimeout(typingOffTimerRef.current);
      typingOffTimerRef.current = window.setTimeout(() => {
        notifyTyping(false);
      }, 2500);
    } else {
      notifyTyping(false);
      if (typingOffTimerRef.current !== null) {
        window.clearTimeout(typingOffTimerRef.current);
        typingOffTimerRef.current = null;
      }
    }
  }
  useEffect(() => {
    return () => {
      if (typingOffTimerRef.current !== null) {
        window.clearTimeout(typingOffTimerRef.current);
        typingOffTimerRef.current = null;
      }
    };
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    const text = composer.trim();
    if (!text) return;
    if (!canSend) return;
    notifyTyping(false);
    send.mutate(text);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 p-3 flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-900">{conv.websiteName}</div>
          <div className="text-xs text-slate-500">{conv.websiteDomain}</div>
        </div>
        <div className="flex items-center gap-2">
          {isResolved ? (
            <Badge tone="gray">Resolved</Badge>
          ) : conv.handler === 'agent' && conv.assignedAgentName ? (
            <Badge tone={isMine ? 'green' : 'amber'}>{conv.assignedAgentName}</Badge>
          ) : null}
          {!isResolved && conv.handler === 'agent' && !isMine && lockOpen ? (
            <Button size="sm" variant="secondary" onClick={() => claim.mutate()} loading={claim.isPending}>
              Claim
            </Button>
          ) : null}
          {!isResolved && conv.handler === 'agent' && isMine ? (
            <Button size="sm" variant="ghost" onClick={() => handback.mutate()} loading={handback.isPending}>
              Hand back to Bruno
            </Button>
          ) : null}
          {canResolve ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowResolveForm((v) => !v)}
              loading={resolve.isPending}
            >
              {showResolveForm ? 'Cancel' : 'Resolve'}
            </Button>
          ) : null}
          {isResolved ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => reopen.mutate()}
              loading={reopen.isPending}
            >
              Reopen
            </Button>
          ) : null}
        </div>
      </div>
      {showResolveForm && canResolve ? (
        <form
          className="border-b border-slate-200 bg-slate-50 p-3 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            resolve.mutate(resolveReason.trim());
          }}
        >
          <input
            value={resolveReason}
            onChange={(e) => setResolveReason(e.target.value)}
            placeholder="Optional resolution note (visible to the visitor)"
            maxLength={500}
            autoFocus
            className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
          <Button type="submit" size="sm" loading={resolve.isPending}>
            Mark resolved
          </Button>
        </form>
      ) : null}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
        {conv.offlineContact ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
            <div className="font-medium text-amber-900">Offline message</div>
            <div className="text-amber-900">From: {conv.offlineContact.name} ({conv.offlineContact.email})</div>
            <div className="text-amber-900 mt-1 whitespace-pre-wrap">{conv.offlineContact.message}</div>
          </div>
        ) : null}
        {detail.messages.map((m) => (
          <MessageBubble key={m.id} message={m} mineUserId={currentUserId} myName={currentUserName} />
        ))}
        {isResolved ? <ResolvedFooter conv={conv} /> : null}
      </div>
      <form className="border-t border-slate-200 p-3 flex items-center gap-2" onSubmit={submit}>
        <input
          value={composer}
          onChange={(e) => handleComposerChange(e.target.value)}
          placeholder={
            isResolved
              ? 'Conversation resolved — reopen to reply'
              : !canSend
                ? conv.handler === 'agent'
                  ? 'Claim the conversation to reply'
                  : 'Conversation is currently with Bruno'
                : 'Reply…'
          }
          disabled={!canSend}
          maxLength={4000}
          className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-slate-50"
        />
        <Button type="submit" disabled={!canSend || !composer.trim()} loading={send.isPending}>
          Send
        </Button>
      </form>
    </div>
  );
}

function ResolvedFooter({ conv }: { conv: ConversationListItem }) {
  const endedAt = conv.endedAt ? new Date(conv.endedAt) : null;
  const who =
    conv.endedBy === 'visitor'
      ? 'the visitor'
      : conv.endedBy === 'agent'
        ? conv.endedByName ?? 'a team member'
        : 'the system';
  return (
    <div className="self-stretch mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
      <div className="font-medium text-slate-700">
        Resolved by {who}
        {endedAt ? ` · ${endedAt.toLocaleString()}` : ''}
      </div>
      {conv.endReason ? (
        <div className="mt-1 whitespace-pre-wrap text-slate-700">{conv.endReason}</div>
      ) : null}
    </div>
  );
}

function MessageBubble({
  message,
  mineUserId,
  myName,
}: {
  message: InboxMessage;
  mineUserId: string;
  myName: string;
}) {
  const role = message.role;
  const author =
    role === 'user'
      ? 'Visitor'
      : role === 'assistant'
        ? 'Bruno'
        : role === 'agent'
          ? message.senderUserId === mineUserId
            ? myName
            : message.senderName ?? 'Support'
          : role === 'system'
            ? 'System'
            : 'Tool';
  const align =
    role === 'user'
      ? 'self-start bg-slate-100 text-slate-900'
      : role === 'agent' && message.senderUserId === mineUserId
        ? 'self-end bg-indigo-600 text-white'
        : role === 'agent'
          ? 'self-end bg-emerald-100 text-emerald-900'
          : role === 'assistant'
            ? 'self-start bg-white border border-slate-200 text-slate-900'
            : 'self-center bg-amber-50 text-amber-900 border border-amber-200';
  return (
    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${align}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">{author}</div>
      {/* CRITICAL: render content via React text node, never via dangerouslySetInnerHTML.
          Visitors and agents both author this content and we keep it plain text. */}
      <div>{message.content ?? ''}</div>
    </div>
  );
}

function SocketStatePill({ state }: { state: AgentSocketState }) {
  const tone =
    state === 'open' ? 'green' : state === 'connecting' ? 'amber' : state === 'closed' ? 'gray' : 'red';
  const label =
    state === 'open' ? 'Realtime' : state === 'connecting' ? 'Connecting…' : state === 'closed' ? 'Offline' : 'Error';
  return <Badge tone={tone}>{label}</Badge>;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface KnownConvSnapshot {
  handler: ConversationListItem['handler'];
  assignedAgentId: string | null;
  agentUnread: number;
  endedAt: string | null;
}

interface SocketHandlerCtx {
  known: Map<string, KnownConvSnapshot>;
  selectedId: string | null;
  currentUserId: string | null;
  onSelect: (id: string) => void;
}

function handleSocketMessage(
  msg: AgentServerMsg,
  qc: ReturnType<typeof useQueryClient>,
  ctx: SocketHandlerCtx,
): void {
  switch (msg.type) {
    case 'inbox.snapshot':
      // Initial load — seed the known map but don't fire pings.
      // The dedicated `useEffect` over `conversations` does the seeding;
      // we just trigger the query refetch here.
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      break;
    case 'inbox.update': {
      const conv = msg.conversation;
      maybeNotifyOnInboxUpdate(conv, ctx);
      // Always remember the latest server state so the next update
      // diffs against fresh data. (`useEffect` over `conversations`
      // catches up on the next render too, but doing it here keeps the
      // notifier accurate even if React batches re-renders.)
      ctx.known.set(conv.id, {
        handler: conv.handler,
        assignedAgentId: conv.assignedAgentId,
        agentUnread: conv.agentUnread,
        endedAt: conv.endedAt,
      });
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', conv.id] });
      break;
    }
    case 'assignment.update':
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', msg.conversationId] });
      break;
    case 'conv.message':
      // Only notify on visitor-authored messages, and never for the
      // chat the agent is actively reading: that one is updating in
      // real-time on screen, so a ping would just be noise.
      if (
        msg.message?.role === 'user' &&
        msg.conversationId !== ctx.selectedId
      ) {
        agentNotifier.notify({
          kind: 'message',
          title: 'New visitor message',
          body:
            (msg.message?.senderName ? `${msg.message.senderName}: ` : '') +
            (msg.message?.content ?? '').slice(0, 120),
          tag: msg.conversationId,
          onClick: () => ctx.onSelect(msg.conversationId),
        });
      }
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', msg.conversationId] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      break;
    case 'presence.update':
      void qc.invalidateQueries({ queryKey: ['team', 'agents'] });
      break;
    case 'handoff.offered':
      // New unclaimed ticket. Always fire the urgent ping so an agent
      // looking at another tab is interrupted; the row will appear
      // under the "Pending handoffs" section once the list refreshes.
      agentNotifier.notify({
        kind: 'handoff',
        title: `Visitor needs a human (${msg.websiteName})`,
        body:
          msg.preview ||
          msg.reason ||
          (msg.visitorPageUrl ? `On ${msg.visitorPageUrl}` : 'Open the inbox to take this conversation.'),
        tag: msg.conversationId,
        onClick: () => ctx.onSelect(msg.conversationId),
      });
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      break;
    case 'handoff.claimed':
      // Someone else claimed (or this tab did). Refresh so the Take
      // button disappears for everyone except the winner.
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', msg.conversationId] });
      break;
    case 'handoff.expired':
      void qc.invalidateQueries({ queryKey: ['inbox', 'list'] });
      void qc.invalidateQueries({ queryKey: ['inbox', 'conv', msg.conversationId] });
      break;
    default:
      break;
  }
}

function maybeNotifyOnInboxUpdate(conv: ConversationListItem, ctx: SocketHandlerCtx): void {
  // Don't notify for the conversation the agent is actively reading;
  // its detail pane already updates in real time.
  if (ctx.selectedId === conv.id) return;
  // Resolved conversations are never an attention event.
  if (conv.endedAt) return;

  const previous = ctx.known.get(conv.id);
  const isNewToInbox = !previous;

  // 1) Brand-new conversation that already needs a human (a visitor
  //    requested help and the orchestrator promoted it to handler='agent'
  //    OR queued an offline-message ticket).
  const handlerWantsAttention =
    conv.handler === 'agent' || conv.handler === 'offline_message';
  if (isNewToInbox && handlerWantsAttention) {
    agentNotifier.notify({
      kind: 'handoff',
      title:
        conv.handler === 'offline_message'
          ? 'New offline message'
          : 'Visitor needs a human',
      body: conv.lastMessagePreview ?? conv.offlineContact?.message ?? undefined,
      tag: conv.id,
      onClick: () => ctx.onSelect(conv.id),
    });
    return;
  }

  // 2) Conversation just got assigned to the current agent (claim flow
  //    or auto-assign during the steal window). We treat this as a
  //    handoff-grade ping because it's an explicit "this chat is yours
  //    now" event.
  const justAssignedToMe =
    !!ctx.currentUserId &&
    conv.assignedAgentId === ctx.currentUserId &&
    previous?.assignedAgentId !== ctx.currentUserId;
  if (justAssignedToMe) {
    agentNotifier.notify({
      kind: 'handoff',
      title: 'Conversation assigned to you',
      body: conv.lastMessagePreview ?? `From ${conv.websiteName}`,
      tag: conv.id,
      onClick: () => ctx.onSelect(conv.id),
    });
    return;
  }

  // 3) Visitor activity bumped the unread counter on a conversation
  //    we already had in the list. Use the softer message-tone ping.
  const unreadIncreased =
    previous && conv.agentUnread > previous.agentUnread && conv.agentUnread > 0;
  if (unreadIncreased) {
    agentNotifier.notify({
      kind: 'message',
      title: 'New visitor message',
      body: conv.lastMessagePreview ?? `From ${conv.websiteName}`,
      tag: conv.id,
      onClick: () => ctx.onSelect(conv.id),
    });
  }
}
